import { useEffect, useState } from "react";
import { Info, Mail, Pencil, Trash2 } from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { useAuth } from "../../context/AuthContext";
import {
  addSecondaryEmail,
  deleteSecondaryEmail,
  getSecondaryEmails,
  updateSecondaryEmail,
} from "./settings-api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AlertsTab() {
  const toast = useToast();
  const { token, user, refreshUser } = useAuth() as any;

  const [emails, setEmails] = useState<string[]>(user?.secondary_emails || []);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const [editTarget, setEditTarget] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    getSecondaryEmails(token)
      .then((res) => setEmails(res.secondary_emails || []))
      .catch(() => {
        /* fall back to whatever the auth context already had */
      })
      .finally(() => setLoading(false));
  }, [token]);

  const syncContext = () => {
    // refreshUser(explicitToken?, options?) — force past the 30s cooldown so
    // other surfaces (e.g. anything reading user.secondary_emails) update now.
    refreshUser?.(undefined, { force: true })?.catch(() => {});
  };

  const handleAdd = async () => {
    const value = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      toast.danger("Enter a valid email address");
      return;
    }
    if (!token) {
      toast.danger("Authentication required");
      return;
    }
    try {
      setBusy(true);
      const res = await addSecondaryEmail(value, token);
      setEmails(res.secondary_emails || []);
      toast.success("Secondary email added");
      setAddOpen(false);
      setNewEmail("");
      syncContext();
    } catch (err: any) {
      toast.danger(err.response?.data?.detail || "Failed to add email");
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    const value = editValue.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      toast.danger("Enter a valid email address");
      return;
    }
    if (value === editTarget) {
      setEditTarget(null);
      return;
    }
    if (!token) {
      toast.danger("Authentication required");
      return;
    }
    try {
      setBusy(true);
      const res = await updateSecondaryEmail(editTarget, value, token);
      setEmails(res.secondary_emails || []);
      toast.success("Secondary email updated");
      setEditTarget(null);
      syncContext();
    } catch (err: any) {
      toast.danger(err.response?.data?.detail || "Failed to update email");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !token) return;
    try {
      setBusy(true);
      const res = await deleteSecondaryEmail(deleteTarget, token);
      setEmails(res.secondary_emails || []);
      toast.success("Secondary email removed");
      setDeleteTarget(null);
      syncContext();
    } catch (err: any) {
      toast.danger(err.response?.data?.detail || "Failed to remove email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Alert Emails</h2>
        <button
          onClick={() => {
            setNewEmail("");
            setAddOpen(true);
          }}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
        >
          Add Email
        </button>
      </div>

      <p className="flex items-start gap-2 rounded-lg border border-brand/30 bg-brand-soft px-4 py-3 text-sm text-ink-2">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        Secondary emails receive the same credit and low-balance alert notifications as your primary account email.
      </p>

      <div className="overflow-hidden rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524]">
        <table className="w-full text-sm">
          <thead className="bg-soft text-ink-3">
            <tr>
              <th className="px-4 py-3 text-left font-mono text-xs font-semibold uppercase tracking-wider">Secondary Email</th>
              <th className="px-4 py-3 text-right font-mono text-xs font-semibold uppercase tracking-wider w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="border-t border-line">
                <td colSpan={2} className="px-4 py-6 text-center text-ink-3">Loading…</td>
              </tr>
            ) : emails.length === 0 ? (
              <tr className="border-t border-line">
                <td colSpan={2} className="px-4 py-6 text-center text-ink-3">
                  No secondary emails yet. Add one to receive alert notifications.
                </td>
              </tr>
            ) : (
              emails.map((email) => (
                <tr key={email} className="border-t border-line hover:bg-soft/50">
                  <td className="px-4 py-3.5 text-ink-2">
                    <span className="flex items-center gap-2">
                      <Mail className="h-4 w-4 shrink-0 text-ink-3" />
                      {email}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditTarget(email);
                          setEditValue(email);
                        }}
                        className="rounded-lg border border-line bg-soft px-2.5 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-line"
                        aria-label={`Edit ${email}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(email)}
                        className="rounded-lg border border-line bg-soft px-2.5 py-1.5 text-xs font-semibold text-danger transition-colors hover:bg-danger/10"
                        aria-label={`Delete ${email}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <Modal
          title="Add Secondary Email"
          onClose={() => setAddOpen(false)}
          width="max-w-md"
          footer={
            <>
              <button
                onClick={() => setAddOpen(false)}
                className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft"
              >
                Cancel
              </button>
              <button
                disabled={busy || !newEmail.trim()}
                onClick={handleAdd}
                className={
                  busy || !newEmail.trim()
                    ? "cursor-not-allowed rounded-lg bg-line px-4 py-2 text-sm font-semibold text-ink-3"
                    : "rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
                }
              >
                {busy ? "Adding…" : "Add Email"}
              </button>
            </>
          }
        >
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Email</span>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              placeholder="e.g., alerts@company.com"
              className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </label>
        </Modal>
      )}

      {editTarget && (
        <Modal
          title="Edit Secondary Email"
          onClose={() => setEditTarget(null)}
          width="max-w-md"
          footer={
            <>
              <button
                onClick={() => setEditTarget(null)}
                className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft"
              >
                Cancel
              </button>
              <button
                disabled={busy || !editValue.trim()}
                onClick={handleEdit}
                className={
                  busy || !editValue.trim()
                    ? "cursor-not-allowed rounded-lg bg-line px-4 py-2 text-sm font-semibold text-ink-3"
                    : "rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
                }
              >
                {busy ? "Saving…" : "Save Changes"}
              </button>
            </>
          }
        >
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Email</span>
            <input
              type="email"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleEdit()}
              className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </label>
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          title="Remove Secondary Email"
          onClose={() => setDeleteTarget(null)}
          width="max-w-md"
          footer={
            <>
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft"
              >
                Cancel
              </button>
              <button
                disabled={busy}
                onClick={handleDelete}
                className={
                  busy
                    ? "cursor-not-allowed rounded-lg bg-line px-4 py-2 text-sm font-semibold text-ink-3"
                    : "rounded-lg bg-danger px-4 py-2 text-sm font-semibold text-white hover:bg-danger/90"
                }
              >
                {busy ? "Removing…" : "Remove"}
              </button>
            </>
          }
        >
          <p className="text-sm text-ink-2">
            Stop sending alert notifications to <span className="font-semibold text-ink">{deleteTarget}</span>?
          </p>
        </Modal>
      )}
    </div>
  );
}
